import { IUser } from '@/types/user';
import {
  ITrainerAdminContent,
  ITrainersContent,
  ITrainersTool,
} from './trainers';

export enum ReviewOutcome {
  Approved = 'approved',
  ApprovedHighQuality = 'approvedHighQuality',
  AutoRejectedDuplicate = 'autoRejectedDuplicate',
  AutoRejectedQuality = 'autoRejectedQuality',
  RejectedAccuracy = 'rejectedAccuracy',
  RejectedCompliance = 'rejectedCompliance',
  RejectedDuplicate = 'rejcetedDuplicate',
  RejectedOther = 'rejectedOther',
  RejectedQuality = 'rejectedQuality',
}

export interface ImageNote {
  originalImageUrl: string;
  noteBase64?: string;
  noteUrl: string;
}

export interface Review {
  _id: string;
  uploadedCollection: string;
  outcome: ReviewOutcome;
  notes?: string;
  imageNotes?: ImageNote[];
}

export interface IMarker {
  id: number;
  x: number;
  y: number;
  status: 'approved' | 'forReview' | 'rejected' | 'created';
  type: 'plate' | 'face';
}

export interface IBenchmarkResponse {
  _id: string;
  reviewer: string;
  value: string;
  expectedValue: string;
  correct: boolean;
  reviewableElt: string;
  tool: string;
  createdAt: string;
  updatedAt: string;
}

export interface AITrainerUserAuditData {
  user: IUser;
  reviewerWeight: number;
  reports: AITrainerUserAuditDataReport[];
}

export interface AITrainerUserAuditDataReport {
  reviewableElement: IReviewableElement;
  filteredConsensusReviews: IConsensusReview[];
  filteredConsensusOutcomes: IConsensusOutcome[];
  imageUrls: string[];
  qaToolName: string;
  element: any;
  objectDetection: IObjectDetection | null;
  qaTool?: ITrainersTool;
  qaToolContentAndAdminContent?: IToolViewerContent;
}

export interface IToolViewerContent {
  qaToolContent: ITrainersContent;
  qaToolAdminContent: ITrainerAdminContent;
}

export interface IReviewableElement {
  _id: string;
  createdAt: Date;
  updatedAt: Date;
  consensusReached: boolean;
  consensusReviewers: string[];
  elt: string;
  h6CellsPoly: any;
  geo: any;
  reviewable?: boolean;
  numRetries?: number;
  readyForConsensus?: boolean;
  priority?: number;
  numConsensusReviewers: number;
  type: string;
  tool?: string;
  createdFrom: string;
}

export interface IProposedBenchmark {
  _id: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string; // ID of user who created it.
  tool: string; // The ID of the QATool game the benchmark is for.
  type: string; // The type (AKA Mongo collection) of the benchmark element.
  elt: string; // The ID of the benchmark element. (i.e. its Mongo ID).
  expectedValidatedResponse: any; // The response we expect to get from the user when they see this benchmark.
  voidedAt: Date | null;
  voidedBy: string | null;
}

export interface IConsensusReview {
  _id: string;
  createdAt: Date;
  updatedAt: Date;
  contributor?: string;
  reviewer: IUser | string;
  outcome: any;
  report?: string;
  reviewableElt?: string;
  frame: string;
  points?: (any | null)[];
  annotations?: any[];
  value?: string;
  ignore?: boolean;
  indexes?: number[];
  tool?: string;
  reviewerWeight?: number;
  weekOf: string;
  rewardPoints: number;
  pos?: any;
}

export interface IAdminReview {
  _id: string;
  reviewer: string;
  reviewableElt: string;
  tool: string;
  correct: number;
  incorrect: number;
  valid: boolean;
  value: any;
  type: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface IConsensusOutcome {
  _id: string;
  createdAt: Date;
  updatedAt: Date;
  frameKmJob?: string;
  frame: string;
  tool: string;
  objectDetection?: string;
  reviewableElt?: string;
  badData?: boolean;
  falsePositive?: boolean;
  location?: any;
  detectionId?: number;
  winningVotes: number;
  totalVotes: number;
  value?: string;
  metadata?: any;
  voidedAt: Date;
  voidedBy?: string;
}

interface IObjectDetection {
  _id: string;
  createdAt: Date;
  updatedAt: Date;
  frame: string;
  landmark?: string;
  mlJob?: string;
  mlModel: string;
  classId: number;
  subClassLabels?: string[];
  x: number;
  y: number;
  w: number;
  h: number;
  confidence: number;
  did: number;
  mosaicEligible?: boolean;
  numMosaics?: number;
  ulgs?: boolean;
  reviewableElements: string[];
}
